/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, NgZone} from '@angular/core';
import {AdnListHelperService} from './list-helper.service';
import {AngularViewRefreshService} from '../util/angular-view-refresh.service';

export interface AdnResourceListConfig {
  afterFill?: (items?: any) => void;
  afterBulkAction?: (...args: any[]) => void;
  afterAction?: (...args: any[]) => void;
  listMethod?: string;
  listGetMethod?: string;
  moreParams?: Record<string, any>;
  viewModel?: string;
  pageSize?: number;
  selectIn?: Record<string, any>;
  noInitQuery?: boolean;
  filterBy?: string;
  filterByValue?: string;
  filterByLike?: string;
  where?: string;
  onlyMine?: boolean;
  concise?: boolean;
  baseTable?: string;
  childCall?: boolean;
  id?: string[];
  excludeIfMissing?: boolean;
  getDefaultOrderByParam?: () => string;
  paginate?: boolean;
  [key: string]: any;
}

/**
 * Angular facade over AdnListHelperService. Keeps the existing list object shape
 * while ensuring callbacks and query follow-ups run inside NgZone.
 */
@Injectable({
  providedIn: 'root'
})
export class AdnResourceListService {
  private readonly queryMethods = [
    'makeQuery',
    'changePage',
    'makeFilterQuery',
    'searchList',
    'makeDateQuery',
    'makeSelectInQuery',
    'makeIncludeQuery',
    'makeSortQuery',
    'makeTargetingQuery',
    'makeRetailProductQuery',
    'makeCountryTeamQuery',
    'makeTeamQuery',
    'makeOrderQuery'
  ];

  constructor(
    private readonly adnListHelper: AdnListHelperService,
    private readonly ngZone: NgZone,
    private readonly viewRefresh: AngularViewRefreshService
  ) {
  }

  createBasicList(list: any, ResourceFactory: any, listId: string, config: AdnResourceListConfig = {}): any {
    this.adnListHelper.setUpBasicList(list, ResourceFactory, listId, this.wrapConfig(config));
    this.wireListToAngular(list);
    return list;
  }

  createList(list: any, ResourceFactory: any, readOnly: boolean, listId: string, config: AdnResourceListConfig = {}): any {
    this.adnListHelper.setUpList(list, ResourceFactory, readOnly, listId, this.wrapConfig(config));
    this.wireListToAngular(list);
    return list;
  }

  private wrapConfig(config: AdnResourceListConfig): AdnResourceListConfig {
    const wrapped = {...config};
    if (config.afterFill) {
      wrapped.afterFill = (items?: any) => {
        this.ngZone.run(() => config.afterFill?.(items));
      };
    }
    if (config.afterBulkAction) {
      wrapped.afterBulkAction = (...args: any[]) => {
        this.ngZone.run(() => config.afterBulkAction?.(...args));
      };
    }
    if (config.afterAction) {
      wrapped.afterAction = (...args: any[]) => {
        this.ngZone.run(() => config.afterAction?.(...args));
      };
    }
    return wrapped;
  }

  ensureAngularList(list: any): void {
    this.wireListToAngular(list);
  }

  private wireListToAngular(list: any): void {
    if (!list || list.__adnAngularListWired) {
      return;
    }
    Object.defineProperty(list, '__adnAngularListWired', {value: true, enumerable: false});

    this.queryMethods.forEach((methodName) => {
      const original = list[methodName];
      if (typeof original !== 'function') {
        return;
      }
      list[methodName] = (...args: any[]) => {
        let result: any;
        this.ngZone.run(() => {
          result = original.apply(list, args);
        });
        this.afterListQuery(result);
        return result;
      };
    });
  }

  afterListQuery(query: any): void {
    const promise = query?.$promise || query;
    if (promise?.then) {
      promise.then(
        () => this.refreshAngularView(),
        () => this.refreshAngularView()
      );
      return;
    }
    this.refreshAngularView();
  }

  refreshAngularView(): void {
    // AdnHttp already schedules this after settle; coalesce is cheap for other callers.
    this.viewRefresh.schedule();
  }
}
